import { CaseTypeEnum, GlobalSettingEnum } from 'api/model';
import KeyValueList from 'components/KeyValueList';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';
import useUsers from 'contexts/basicData/useUsers';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import { useMemo } from 'react';
import styled from 'styled-components';
import useGlobalSettingsForm from '../GlobalSettings/useGlobalSettingsForm';

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
`;

/**
 * Ruttplaneringens globala inställningar, flyttade hit från Globala inställningar. Samma
 * formulärhook som där, så sparbeteendet är detsamma (multi-selects sparas via spar-ikonen).
 * Bara SuperAdmin får skriva — GlobalSettingsController prövar det per nyckel.
 *
 * Listan som släpper fram den här fliken (116) ligger kvar under Globala inställningar, så att
 * SuperAdmin inte kan låsa ute sig själv härifrån.
 */
const RoutePlanningGlobalSettings = () => {
  const users = useUsers();
  const userOptions = useMemo(
    () =>
      Object.values(users ?? {})
        .filter((u) => u.isActive)
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
        .map((user) => ({ label: user.name, value: user.userID })),
    [users]
  );

  // Företag: kunder som aldrig får ombokningsförslag (108) och platser som alltid planeras manuellt (111).
  const { companies } = useCompanies();
  const companyOptions = useMemo(
    () =>
      (companies ?? [])
        .filter((c) => c.active)
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
        .map((c) => ({ label: c.name, value: c.companyID })),
    [companies]
  );

  // Ärendetyperna med ett kundben: de enda minsta tiden hos kunden (125) kan gälla.
  const { caseTypes } = useTranslations();
  const customerVisitCaseTypeOptions = useMemo(
    () =>
      [
        CaseTypeEnum.HomeDelivery,
        CaseTypeEnum.Purchase,
        CaseTypeEnum.TradeIn,
        CaseTypeEnum.Auction,
      ].map((caseType) => ({
        label: caseTypes?.[caseType] ?? String(caseType),
        value: caseType,
      })),
    [caseTypes]
  );

  // Länen för storstadslänen (35) och inköpens fasta framförhållning (100). Namn och inte id:n:
  // samma län ligger på flera postnummerrader (Stockholm har id 1 och 28), och servern matchar på namnet.
  const { zipCodeAreaByName } = useZipCodeAreas();
  const countyOptions = useMemo(
    () =>
      Object.keys(zipCodeAreaByName ?? {})
        .filter((name) => name)
        .sort((a, b) => a.localeCompare(b, 'sv'))
        .map((name) => ({ label: name, value: name })),
    [zipCodeAreaByName]
  );

  const {
    makeGlobalSettingsCheckbox,
    makeGlobalSettingsInput,
    mkGlobalSettingsSelect,
  } = useGlobalSettingsForm();

  return (
    <Wrapper>
      <KeyValueList
        title="Ruttplanering"
        colonKey
        keyWidth="700px"
        maxKeyWidth="700px"
        rows={[
          mkGlobalSettingsSelect(
            GlobalSettingEnum.UsersWithAccessToRoutePlanApply,
            userOptions,
            'number'
          ),
          makeGlobalSettingsCheckbox(
            GlobalSettingEnum.RoutePlanAbsoluteTbEnabled
          ),
          mkGlobalSettingsSelect(
            GlobalSettingEnum.CompanyIdsWithoutRebookingSuggestions,
            companyOptions,
            'number'
          ),
          mkGlobalSettingsSelect(
            GlobalSettingEnum.CompanyIdsAlwaysPlannedManually,
            companyOptions,
            'number'
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanFacilityDeliveryHourlyRateSek,
            'number',
            { min: 0, max: 5000, integer: true }
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanMaxWalkMinutes,
            'number',
            { min: 0, max: 240, integer: true }
          ),
          // Delad med resten av systemet (FT:s hemområde), så den ligger kvar under Globala
          // inställningar också. Här styr den storstadsregionerna och momentschablonen.
          mkGlobalSettingsSelect(
            GlobalSettingEnum.CountyNamesCountedAsLargeCities,
            countyOptions,
            'string'
          ),
        ]}
      />
      <KeyValueList
        title="Inköp och auktionsbilar"
        colonKey
        keyWidth="700px"
        maxKeyWidth="700px"
        rows={[
          // Tom lista = regeln av: alla inköp får helperns procentandel före bokad till.
          mkGlobalSettingsSelect(
            GlobalSettingEnum.CountyNamesWithFixedPurchaseLeadTime,
            countyOptions,
            'string'
          ),
          // Glesa delar av ett valt län (Norrtälje 761, Rimbo 762, Åkersberga 184) behåller
          // procentandelen. Fritext: kommaseparerade postnummerprefix.
          makeGlobalSettingsInput(
            GlobalSettingEnum.ZipPrefixesExemptFromFixedPurchaseLeadTime,
            'text'
          ),
        ]}
      />
      <KeyValueList
        title="Minsta tid hos kunden"
        colonKey
        keyWidth="700px"
        maxKeyWidth="700px"
        rows={[
          // Tomt = 60 min och alla fyra typerna; 0 min stänger av regeln.
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanMinCustomerVisitMinutes,
            'number',
            { min: 0, max: 480, integer: true }
          ),
          mkGlobalSettingsSelect(
            GlobalSettingEnum.RoutePlanMinCustomerVisitCaseTypes,
            customerVisitCaseTypeOptions,
            'number'
          ),
        ]}
      />
      <KeyValueList
        title="Ombokningsförslag"
        colonKey
        keyWidth="700px"
        maxKeyWidth="700px"
        rows={[
          // Tomt fält = appsettings (60 / 60 / 30). Förslagen är alltid förslag: TL ringer och svarar.
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanRebookingWaitThresholdMinutes,
            'number',
            { min: 0, max: 1440, integer: true }
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanRebookingMaxShiftMinutes,
            'number',
            { min: 15, max: 1440, integer: true }
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanRebookingMinReductionMinutes,
            'number',
            { min: 0, max: 1440, integer: true }
          ),
        ]}
      />
      <KeyValueList
        title="Ruttprov och ruttfeedback"
        colonKey
        keyWidth="700px"
        maxKeyWidth="700px"
        rows={[
          mkGlobalSettingsSelect(
            GlobalSettingEnum.RoutePlanFeedbackNameViewers,
            userOptions,
            'number'
          ),
          mkGlobalSettingsSelect(
            GlobalSettingEnum.RoutePlanFeedbackToastExcluded,
            userOptions,
            'number'
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanFeedbackDismissalsBeforePause,
            'number',
            { min: 1, max: 100, integer: true }
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanFeedbackPauseDays,
            'number',
            { min: 0, max: 365, integer: true }
          ),
          makeGlobalSettingsInput(
            GlobalSettingEnum.RoutePlanFeedbackSampleSize,
            'number',
            { min: 1, max: 50, integer: true }
          ),
        ]}
      />
    </Wrapper>
  );
};

export default RoutePlanningGlobalSettings;
